import React, { useEffect } from "react";
import { type AuthUser } from "wasp/auth";
import DropdownUser from "../../user/DropdownUser";
import { cn } from "../../client/cn";
import { useDebugMode } from "../../shared/hooks";
import { NavLink } from "react-router-dom";
import FeedbackDialog from "./FeedbackDialog";
import { LanguageSelector } from "../../components/ui/language-selector";
import { useTranslation } from "../../i18n/hooks";

const Header = (props: {
  sidebarOpen: string | boolean | undefined;
  setSidebarOpen: (isOpen: boolean) => void;
  user: AuthUser;
}): React.JSX.Element => {
  const isDebugMode = useDebugMode();
  const { t } = useTranslation();
  
  // Twitter conversion tracking
  useEffect(() => {
    // Create Twitter script element
    const script = document.createElement('script');
    script.innerHTML = `
      !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
      },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
      a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
      twq('config','pjhak');
    `;
    
    // Append script to document head
    document.head.appendChild(script);
    
    // Cleanup function to remove the script when component unmounts
    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount
  
  // Meta Pixel tracking
  useEffect(() => {
    // Create Meta Pixel script element
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1748335599367304');
      fbq('track', 'PageView');
    `;
    
    // Add Meta Pixel noscript element
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1748335599367304&ev=PageView&noscript=1';
    noscript.appendChild(img);
    
    // Append elements to document
    document.head.appendChild(script);
    document.body.appendChild(noscript);
    
    // Cleanup function
    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
      if (noscript.parentNode) {
        document.body.removeChild(noscript);
      }
    };
  }, []);
  
  return (
    <header className="sticky top-0 z-[10] md:z-[59] flex w-full bg-[#1A1A1A] dark:bg-boxdark dark:drop-shadow-none">
      <div className="flex flex-grow items-center justify-between px-4  py-2 md:px-8 md:py-4 shadow">
        {/* Left Side */}
        <div className="flex items-center gap-4">
          {/* Hamburger Toggle BTN */}
          <button
            aria-controls="sidebar"
            onClick={(e) => {
              e.stopPropagation();
              props.setSidebarOpen(!props.sidebarOpen);
            }}
            className="z-99999 block rounded-sm border border-stroke bg-white p-[4px] md:p-1.5 shadow-sm dark:border-strokedark dark:bg-boxdark lg:hidden"
          >
            <span className="relative block h-5.5 w-5.5 cursor-pointer">
              <span className="du-block absolute right-0 h-full w-full">
                <span
                  className={cn(
                    "relative top-0 left-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-[0] duration-200 ease-in-out dark:bg-white",
                    {
                      "!w-full delay-300": !props.sidebarOpen,
                    }
                  )}
                ></span>
                <span
                  className={cn(
                    "relative top-0 left-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-150 duration-200 ease-in-out dark:bg-white",
                    {
                      "delay-400 !w-full": !props.sidebarOpen,
                    }
                  )}
                ></span>
                <span
                  className={cn(
                    "relative top-0 left-0 my-1 block h-0.5 w-0 rounded-sm bg-black delay-200 duration-200 ease-in-out dark:bg-white",
                    {
                      "!w-full delay-500": !props.sidebarOpen,
                    }
                  )}
                ></span>
              </span>
              <span className="absolute right-0 h-full w-full rotate-45">
                <span
                  className={cn(
                    "absolute left-2.5 top-0 block h-full w-0.5 rounded-sm bg-black delay-300 duration-200 ease-in-out dark:bg-white",
                    {
                      "!h-0 !delay-[0]": !props.sidebarOpen,
                    }
                  )}
                ></span>
                <span
                  className={cn(
                    "delay-400 absolute left-0 top-2.5 block h-0.5 w-full rounded-sm bg-black duration-200 ease-in-out dark:bg-white",
                    {
                      "!h-0 !delay-200": !props.sidebarOpen,
                    }
                  )}
                ></span>
              </span>
            </span>
          </button>

          {/* Logo */}
          <NavLink to="/admin/dashboard" className="hidden lg:flex items-center">
            <img 
              src="https://www.marketplaceiq.io/wp-content/uploads/2024/12/boldlogo.png" 
              alt={t('header.logo_alt')}
              className="h-5 w-auto hover:opacity-90 transition-opacity"
              style={{
                filter: 'brightness(0) invert(1)' // Makes the logo white
              }}
            />
          </NavLink>
        </div>

        {/* Right Side */}
        <div className="flex items-center justify-end flex-1 gap-3">
          {isDebugMode && (
            <div className="bg-red-500 text-white px-4 py-1 rounded">
              {t('header.debug_mode')}
            </div>
          )}

          <div className="flex items-center gap-6">
            <LanguageSelector /> 
            {/* Docs Link */}
            <a 
              href="https://www.marketplaceiq.io/docs/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:block text-white hover:text-gray-300 transition-colors"
            >
              {t('common.docs')}
            </a>
            
            {/* Feedback Dialog */}
            <FeedbackDialog />
            
            {/* User Dropdown */}
            <DropdownUser />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;